import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import Modal from '../Modal';
import SuccessoIcon from '../Icons/SuccessoIcon';
import ErroreIcon from '../Icons/ErroreIcon';
import InfoIcon from '../Icons/InfoesIcon';
import { createModalPromise } from '../Utils/modals';
import FooterButton from './FooterButton';

const Confirm = ({
  className,
  message,
  type, confirmPrimary,
  testoConferma, confirmText, cancelText,
  title,
  onConfirm, onCancel,
  show,
  onClose,
  dontShowAgainId,
}) => {
  const [working, setWorking] = useState(false);
  const refMainButton = useRef();

  useEffect(() => {
    setTimeout(() => {
      refMainButton.current && refMainButton.current.focus();
    }, 0);
  }, []);

  const renderIcon = useCallback(() => {
    if (type === 'success') {
      return <SuccessoIcon />;
    }
    if (type === 'error' || type === 'warning') {
      return <ErroreIcon />;
    }
    if (type === 'info') {
      return <InfoIcon />;
    }
    return null;
  }, [type]);

  const annulla = useCallback(() => {
    if (onCancel) {
      const res = onCancel();
      if (res && res.then) {
        setWorking(true);
        res.then(() => {
          setWorking(false);
          onClose && onClose();
        });
        return;
      }
    }
    onClose && onClose();
  }, [onClose, onCancel]);

  const conferma = useCallback(() => {
    if (onConfirm) {
      const res = onConfirm();
      if (res && res.then) {
        setWorking(true);
        res.then(() => {
          setWorking(false);
          onClose && onClose();
        });
        return;
      }
    }
    onClose && onClose();
  }, [onClose, onConfirm]);

  const classes = useMemo(
    () => cns(
      'confirm',
      {
        'confirm--success': type === 'success',
        'confirm--error': type === 'error',
        'confirm--warning': type === 'warning',
        'confirm--info': type === 'info',
      },
      className,
    ),
    [className, type],
  );

  return (
    <Modal className={classes} doNotFocus onClose={onClose} show={show} dontShowAgainId={dontShowAgainId}>
      <Modal.Header>
        <div className="confirm__icon">{renderIcon()}</div>
        <div className="confirm__title">{title}</div>
      </Modal.Header>
      <Modal.Body>{message}</Modal.Body>
      <Modal.Footer>
        <FooterButton
          onClick={annulla}
          text={cancelText || 'Annulla'}
          disabled={working}
          primary={!confirmPrimary}
        />
        <FooterButton
          onClick={conferma}
          text={confirmText || testoConferma || 'Conferma'}
          disabled={working}
          loading={working}
          primary={confirmPrimary}
        />
      </Modal.Footer>
    </Modal>
  );
};

Confirm.propTypes = {
  /** Classi css personalizzate da aggiungere al componente */
  className: PropTypes.string,
  /** Indica il testo della finestra */
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  /**
   * Callback invocata al click sul pulsante Annulla. Se questo
   * metodo ritorna una *Promise* viene gestito lo spinner nel modale.
   */
  onCancel: PropTypes.func,
  /**
   * Callback invocata al click sul pulsante Conferma. Se questo
   * metodo ritorna una *Promise* viene gestito lo spinner nel modale.
   */
  onConfirm: PropTypes.func.isRequired,
  /**
   * Callback di chiusura della modale.
   */
  onClose: PropTypes.func,
  /** Indica se mostrare o meno la modale */
  show: PropTypes.bool,
  /** @Deprecated: usare confirmText */
  testoConferma: PropTypes.string,
  /** Testo alternativo del pulsante Conferma */
  confirmText: PropTypes.string,
  /** Testo alternativo del pulsante Annulla */
  cancelText: PropTypes.string,
  /** Indica se il bottone confirm è la scelta primaria */
  confirmPrimary: PropTypes.bool,
  /** Indica il titolo della finestra */
  title: PropTypes.string.isRequired,
  /** Indica il tipo di Confirm da renderizzare */
  type: PropTypes.oneOf(['error', 'warning', 'success', 'info']),
  /**
Identificativo del componente per la gestione 'Non visualizzare più'. Se impostato abilita la gestione.
Lo stato è salvato nella sessione locale del browser quindi è necessario un identificativo per gestire il salvataggio.

Tipicamente è composto dall'id dell'utente e da un identificativo del messaggio.
*/
  dontShowAgainId: PropTypes.string,
};

Confirm.defaultProps = {
  className: '',
  testoConferma: undefined,
  cancelText: undefined,
  confirmText: undefined,
  type: 'error',
  show: true,
  onClose: null,
  onCancel: null,
  confirmPrimary: true,
  dontShowAgainId: undefined,
};

Confirm.createModalPromise = (props) => createModalPromise(Confirm, props, 'temp-modal-confirm');

export default Confirm;
